'use client';

import React, { useState } from 'react';
import { Search, MapPin, Calendar, Users } from 'lucide-react';
import { useRouter } from 'next/navigation';

export default function HeroSearch() {
  const router = useRouter();
  const [city, setCity] = useState('');
  const [date, setDate] = useState('');
  const [players, setPlayers] = useState('2');

  return (
    <section
      className="relative overflow-hidden py-16 lg:py-24"
      style={{
        backgroundImage: "url('/assets/images/tunisia_padel_banner.png')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {/* Dark overlay for text readability */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#FF2D1F]/85 via-[#cc1a0e]/75 to-[#8a0f08]/80" />
      {/* Background pattern */}
      <div className="absolute top-8 right-20 w-64 h-64 rounded-full border-2 border-white/40" />
      <div className="absolute top-20 right-40 w-40 h-40 rounded-full border border-white/30" />
      <div className="absolute -bottom-10 left-20 w-80 h-80 rounded-full border border-white/20" />
      <div className="absolute bottom-10 left-10 w-32 h-32 rounded-full border-2 border-white/30" />
      <div className="relative max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16">
        <div className="max-w-3xl">
          {/* Eyebrow */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 border border-white/25 mb-5">
            <span className="w-2 h-2 rounded-full bg-secondary animate-pulse-subtle" />
            <span className="text-sm font-medium text-white/90">Tunisia's #1 Padel Booking Platform</span>
          </div>

          <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight tracking-tight mb-4">
            Book Your Perfect
            <br />
            <span className="text-secondary">Padel Court</span> in Tunisia
          </h1>
          <p className="text-lg text-white/75 mb-10 max-w-xl leading-relaxed">
            Discover courts at Tunisia's finest hotels — from Hammamet to Sousse. Reserve in minutes, play the same day.
          </p>

          {/* Search Bar */}
          <div className="bg-white rounded-2xl shadow-modal p-2 flex flex-col sm:flex-row gap-2">
            {/* City */}
            <div className="flex items-center gap-3 flex-1 px-4 py-3 rounded-xl hover:bg-muted/50 transition-colors cursor-pointer">
              <MapPin size={18} className="text-primary flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-0.5">Location</p>
                <input
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e?.target?.value)}
                  placeholder="Hammamet, Sousse, Tunis..."
                  className="w-full text-sm font-medium text-foreground bg-transparent border-0 focus:outline-none placeholder:text-muted-foreground/60"
                />
              </div>
            </div>

            <div className="hidden sm:block w-px bg-border self-stretch my-2" />

            {/* Date */}
            <div className="flex items-center gap-3 flex-1 px-4 py-3 rounded-xl hover:bg-muted/50 transition-colors cursor-pointer">
              <Calendar size={18} className="text-primary flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-0.5">Date</p>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e?.target?.value)}
                  className="w-full text-sm font-medium text-foreground bg-transparent border-0 focus:outline-none"
                />
              </div>
            </div>

            <div className="hidden sm:block w-px bg-border self-stretch my-2" />

            {/* Players */}
            <div className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-muted/50 transition-colors cursor-pointer">
              <Users size={18} className="text-primary flex-shrink-0" />
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-0.5">Players</p>
                <select
                  value={players}
                  onChange={(e) => setPlayers(e?.target?.value)}
                  className="text-sm font-medium text-foreground bg-transparent border-0 focus:outline-none cursor-pointer"
                >
                  {['2', '3', '4']?.map((n) => (
                    <option key={`players-${n}`} value={n}>{n} Players</option>
                  ))}
                </select>
              </div>
            </div>

            {/* CTA */}
            <button
              onClick={() => router?.push('/court-discovery')}
              className="flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 active:scale-95 transition-all duration-150 shadow-sm flex-shrink-0"
            >
              <Search size={16} />
              Search Courts
            </button>
          </div>

          {/* Quick Links */}
          <div className="flex flex-wrap gap-2 mt-5">
            {['Hammamet', 'Sousse', 'Port El Kantaoui', 'Tunis', 'Djerba']?.map((loc) => (
              <button
                key={`quick-${loc}`}
                className="px-3 py-1.5 rounded-full bg-white/15 border border-white/25 text-sm text-white/85 hover:bg-white/25 transition-all duration-150"
              >
                {loc}
              </button>
            ))}
          </div>
        </div>
      </div>
      {/* Stats bar */}
      <div className="relative max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16 mt-12">
        <div className="flex flex-wrap gap-8">
          {[
            { value: '14', label: 'Partner Hotels' },
            { value: '32', label: 'Padel Courts' },
            { value: '2,400+', label: 'Sessions Booked' },
            { value: '4.8★', label: 'Average Rating' },
          ]?.map((stat) => (
            <div key={`stat-${stat?.label}`}>
              <p className="text-2xl font-bold text-white font-mono tabular-nums">{stat?.value}</p>
              <p className="text-sm text-white/65">{stat?.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}